import React, { useCallback } from 'react';
import { TextV1 } from '@withjoy/joykit';
import type { ExtendedOverlayableProps } from '@withjoy/joykit';
import { DialogV1, useOverlayUnstable } from '@withjoy/joykit';
import type { EventPickerProps, UseEventPickerControllerArgs } from './EventPicker.controller';
import { useEventPickerController } from './EventPicker.controller';
import { EventPicker } from './EventPicker';
import { StyledDialog } from './EventPicker.styles';
import type { EventMembership } from '@shared/components/AuthProvider';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useTranslation } from '@shared/core';

const OVERLAY_KEY = 'event-picker';

export const EventPickerDialogContent: React.FC<EventPickerDialogContentProps & ExtendedOverlayableProps> = props => {
  const { overlayKey, hide, handleOnEventClick, onDismiss } = props;
  const { tq } = useTranslation('account');

  // Fires on a pick too — the pick path closes the dialog. Consumers that need to tell the two
  // apart should key off the pick, which lands first.
  const onClose = useCallback(() => {
    hide(overlayKey);
    onDismiss?.();
  }, [hide, overlayKey, onDismiss]);

  // Escape and outside-click close through Overlay's own `close()`, which hides the overlay itself
  // and never reaches the header's handler — so report the dismissal without hiding twice.
  const onOverlayClose = useCallback(() => {
    onDismiss?.();
  }, [onDismiss]);

  const onEventClickAndHide = useCallback<typeof handleOnEventClick>(
    (...args) => {
      handleOnEventClick(...args);
      onClose();
    },
    [handleOnEventClick, onClose],
  );

  return (
    <StyledDialog
      overlayKey={overlayKey}
      canCloseOnOutsideClick={true}
      canCloseOnEscapeKey={true}
      onClose={onOverlayClose}
    >
      <DialogV1.Header
        title={
          <TextV1
            textAlign="left"
            lineHeight="32px"
            letterSpacing="-0.24px"
            fontWeight={600}
            fontStyle="normal"
            fontSize="24px"
            color={'black'}
            component="h1"
            marginY={6}
            marginX={7}
          >
            {tq`Select an Event`}
          </TextV1>
        }
        onClose={onClose}
      ></DialogV1.Header>
      <DialogV1.Body>
        <EventPicker
          {...props}
          handleOnEventClick={onEventClickAndHide}
        />
      </DialogV1.Body>
    </StyledDialog>
  );
};

type EventPickerDialogContentProps = EventPickerProps & Readonly<{ onDismiss?: () => void }>;

export type UseEventPickerDialogArgs = {
  onOpen?: (memberships: EventMembership[]) => void;
  /** Called whenever the dialog closes, whether or not an event was picked. */
  onDismiss?: () => void;
} & UseEventPickerControllerArgs &
  Pick<EventPickerProps, 'currentEventId' | 'onCreateNewEvent'>;

export const useEventPickerDialog = ({
  onEventClick,
  onOpen,
  onDismiss,
  membershipStatus,
  currentEventId,
  onCreateNewEvent,
  blockIntlRestrictedEvents,
}: UseEventPickerDialogArgs) => {
  const { handleOnEventClick, eventMemberships, intlRestrictedEventIds, unselectableEventIds } = useEventPickerController({
    onEventClick,
    membershipStatus,
    blockIntlRestrictedEvents,
  });
  const [showEventPickerDialog, hideEventPickerDialog] = useOverlayUnstable<EventPickerDialogContentProps>(
    {
      overlayKey: OVERLAY_KEY,
      render: props => <EventPickerDialogContent {...props} />,
      props: {
        handleOnEventClick,
        eventMemberships,
        currentEventId,
        onCreateNewEvent,
        intlRestrictedEventIds,
        unselectableEventIds,
        onDismiss,
      },
    },
    {
      dependencies: [currentEventId, eventMemberships, handleOnEventClick, onCreateNewEvent, intlRestrictedEventIds, unselectableEventIds, onDismiss],
    },
  );

  const wrappedShowEventPicker = useEventCallback(() => {
    onOpen?.(eventMemberships);
    showEventPickerDialog();
  });

  return [wrappedShowEventPicker, hideEventPickerDialog];
};
